import { useEffect, useState, useCallback } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { DRIVE_TITLE_ID, UNIFY_ADMIN_ROUTERS, URL_ROUTES, REACT_APP_DWM_ID } from '../constants';
import ApplicationsTab from './ApplicationsTab/ApplicationsTab';
import MyUnifyTab from './MyUnifyTab/MyUnifyTab';
import WorkflowsTab, { AivaTab } from './WorkflowsTab/WorkflowsTab';
import CardSkeleton from './CardSkeleton/CardSkeleton';
import {
  BypassableRoute,
  BypassableLoginCallback,
} from './common/BypassableRoutes';
import styled from 'styled-components';
import ErrorHandlingDialog from './Dialogs/ErrorDialog';
import { Workflow, WorkflowViewMap } from '../types';
import { useSetDriveAccess } from '../contexts/AppStateContext';
import UnifyMainContainer from './UnifyAdministration/Containers/UnifyMainContainer';
import CMSAdminContainer from './UnifyAdministration/CampaignManagement/Containers/CMSAdminContainer';
import OutageAdminContainer from './UnifyAdministration/Notification/Containers/Outage/OutageAdminContainer';
import UnifyAdminContainer from './UnifyAdministration/Index';
import { useAnalytics } from '../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../constants';
import { useAllApplicationsStore, useApplicationStore, useIamUserProfileStore, useUserSettingsStore, useWorkflowsStore, useWorkflowsViewMapStore,useRouteStore } from '../Store';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import UniPulseMain from './UniPulse/UniPulseMain';
import DealerAdminContainer from './DealerAdmin/DealerAdminContainer';
import GlobalSearchResult from './GlobalSearch/GlobalSearchResult';
import GlobalSearchHistory from './GlobalSearch/GlobalSearchHistory';
import LogoutCallback from './LogoutCallback/LogoutCallback';

/**
 * @desc Routes Paths
 */

const enum ROUTES_PATHS {
  'MY_UNIFY' = 'myUnify',
  'APPLICATIONS' = 'applications',
  'WORKFLOWS' = 'workflows',
  'UNIFY_ADMIN' = 'admin',
}

const RouteContainer = styled.div`
  padding: 0 24px 24px;
  @media (max-width: 500px) {
    font-size: 14px;
  }
`;

export default function Routes() {
  const allApplications = useAllApplicationsStore((state:any) => state.applications);
  const applications = useApplicationStore((state:any) => state.applications);
  const apiStatus = useApplicationStore((state:any) => state.apiStatus);
  const isFetched = useApplicationStore((state:any) => state.isFetched);
  const route =  useRouteStore((state:any) => state.route );
  const [defaultRoute, setDefaultRoute] = useState<string>('/');
  const [showErrorDialog, toggleErrorDialog] = useState(false);
  const filteredApps = applications.filter((app: any) => app.isFavorite);
  const workflows: WorkflowViewMap  = useWorkflowsViewMapStore((state:any) => state.workflowsViewMap);
  const status = useWorkflowsViewMapStore((state:any) => state.apiStatus);
  const userPreferenceSettings = useUserSettingsStore((state:any) => state.userSettings);
  const iamUserInfo = useIamUserProfileStore((state:any) => state.iamUserInfo);
  const adminRole = iamUserInfo.fullUser && iamUserInfo.fullUser.roles && 
                    iamUserInfo.fullUser.roles.find(
                        (role:any) => role.roleName.toLowerCase() === "enterprise administrator");
  const filteredWorkflows = Object.entries(workflows).filter(
    ([_applicationTitle, domain]) => {
      if (Array.isArray(domain)) {
        const filteredDomains = Object.entries(domain).filter(
          ([_domainTitle, _workflow]) => {
            return _workflow.isFavorite;
          }
        );
        return filteredDomains.length > 0;
      } else {
        const filteredDomains = Object.entries(domain).filter(
          ([domainTitle, _workflows]) => {
            return (_workflows as Workflow[]).some(
              (workflow: Workflow) => workflow.isFavorite
            );
          }
        );
        return filteredDomains.length > 0;
      }
    }
  );
  
  const myUnifyWorkflows = useWorkflowsViewMapStore((state:any) => state.favoriteWorkflowsViewMap);
  const { trackEvent } = useAnalytics();
  const userwWorkflows  = useWorkflowsStore((state:any) => state.workflows);
  const driveWorkflows  = userwWorkflows?.filter((workflow: any) => workflow.appId === REACT_APP_DWM_ID)
  const favoritedWorkflows = userwWorkflows?.filter((workflow: any) => workflow.isFavorite);
  const uniPulseFlag = useFeatureFlag('UNIFY_UI_PULSE');
  const setDeepLinkProgress = useAllApplicationsStore((state:any) => state.setDeepLinkProgress);

  const generateDefaultRoute = useCallback(() => {
    /* Note: Unify admin is rendering on a separate route and needs full refresh to update the window location and pathnam
     */

    // If url is base64 encoded;
    if(window.location.pathname?.indexOf('/bdp') >= 0) {
      
      try{
        let pid = 'NONE';
        let link = '';
        let obj = {id:'', url:''};
        try{
          obj = JSON.parse(atob(window.location.pathname.split('/bdp')[1] || '{}'));
          pid = obj.id;
          link = obj.url;
        } catch(_) {
          link = window.location.pathname.split('/bdp')[1];
        }
        //const { id:pid, url:link } = JSON.parse(atob(window.location.pathname.split('/bdp')[1] || '{}'));
        const encLink = `/dp/${pid}/${link}`;
        if(pid && link) {
          window.localStorage.setItem('deep-link-url', encLink);
          setDeepLinkProgress(true);
        }
      } catch (error) {
        console.error('Error parsing deep link from /bdp:', error);
      }
    
    }

    if(window.location.pathname?.indexOf('/dp_') >= 0) {
      const deepLink = window.location.href.split('/dp_')[1];
      window.localStorage.setItem('deep-link-url', deepLink);
      setDeepLinkProgress(true);
    }

    if(window.location.pathname?.indexOf('/dp/') >= 0) {
      window.localStorage.setItem('deep-link-url', window.location.href.substring(window.location.href.indexOf('/dp/')));
      setDeepLinkProgress(true);
    }

    if (window.location.pathname === '/admin') {
      return ROUTES_PATHS.UNIFY_ADMIN;
    }
    if (window.location.pathname === '/pulse') {
      return URL_ROUTES.pulse;
    }
    if (window.location.pathname === '/dealer-admin') {
      return URL_ROUTES.dealerAdmin;
    }
    if (isFetched) {
      if(favoritedWorkflows?.length || filteredApps.length) 
        {
        trackEvent(AMPLITUDE_EVENTS.TAB_SELECTED, {
          'Tab': 'My Unify',
          'Favorited Apps': filteredApps.length,
          'Favorited Workflows': favoritedWorkflows?.length,
          'Is Favorited': true ,
        });
      }
      if (userPreferenceSettings.appView && userPreferenceSettings.appView !== 'default') {
        return userPreferenceSettings.appView === 'unify' ? ROUTES_PATHS.MY_UNIFY : (userPreferenceSettings.appView === 'applications' ? ROUTES_PATHS.APPLICATIONS : ROUTES_PATHS.WORKFLOWS);
      }
      return filteredWorkflows.length || filteredApps.length
        ? ROUTES_PATHS.MY_UNIFY :
        driveWorkflows.length ? 
        ROUTES_PATHS.WORKFLOWS : ROUTES_PATHS.APPLICATIONS;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, driveWorkflows.length]);

  const driveAccess = !!applications.find((app: any) => app.id === DRIVE_TITLE_ID);
  const setDriveAccess = useSetDriveAccess();

  useEffect(() => {
    setDriveAccess && setDriveAccess(driveAccess);
  }, [driveAccess, setDriveAccess]);

  useEffect(() => {
    toggleErrorDialog(apiStatus === 'error');
  }, [apiStatus]);

  useEffect(() => {
    try{
      // when applications are fetched 
      if(applications.length > 0) {
        let dpParam = '';
        const deepLink = localStorage.getItem('deep-link-url') || '';
        if(!deepLink) return;

        const linkParam = deepLink.split('/dp/')[1];
        const pid =  linkParam.substring(0, linkParam.indexOf('/'));
        const deepLinkUrl =  linkParam.substring(linkParam.indexOf('/') + 1);
        // redirected from deep link, set the deep link url in local storage for later use
        if(deepLinkUrl && pid) {
          const pidApp = applications.find((app: any) => app.id === pid);
          if(pidApp) {
            console.log('Deep link matched application:', pidApp);
            dpParam = 'ap_' + pidApp.id;
          }

          // if not found in app, search in wflows
          if(!dpParam) {
            const wf = applications.flatMap((app: any) => app.workflows || []).find((wf: any) => {
              return wf.workflowId === pid;
            });
            if(wf) {
              console.log('Deep link matched workflow:', wf);
              dpParam = 'wf_' + wf.workflowId;
            }
          }

         if(dpParam !== ''){
            const deepLink  = dpParam + '/' + deepLinkUrl;
            deepLink && window.localStorage.setItem('deep-link-url', deepLink);
            setDeepLinkProgress(true);
          }
          else {
            // not matched with any application or workflow, 
            // sending user to the request link URL directly 
            console.log('Deep link did not match any application or workflow');
            localStorage.removeItem('deep-link-url');
            setDeepLinkProgress(false);
            window.location.href = deepLinkUrl;
          }
        }
      }
    }
    catch(e){
      console.log('ERROR in deeplink parsing', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications]);

  useEffect(() => {
    const defaultRoute = generateDefaultRoute();
    if(defaultRoute){
      setDefaultRoute(defaultRoute as unknown as string);
    }
    // in case of deep link
    try{
      const deepLink = localStorage.getItem('deep-link-url');
      if(deepLink) {
        const deepLinkParams = deepLink.substring(0, deepLink.indexOf('/')).split('_');
        const deepLinkAppId = deepLinkParams[0] === 'ap' ? deepLinkParams[1] : '';
        const deepLinkWfId = deepLinkParams[0] === 'wf' ? deepLinkParams[1]: '';
        if(deepLinkAppId) setDefaultRoute('/' + ROUTES_PATHS.APPLICATIONS); 
        if(deepLinkWfId) setDefaultRoute('/' +  ROUTES_PATHS.WORKFLOWS);
      }
    }
    catch(e){
      console.log('ERROR in deeplink parsing')
    }
  }, [generateDefaultRoute]);

  useEffect(() => {
    if(route){
      setDefaultRoute(route);
    }
  },[route]);

  if (apiStatus === 'error') {
    return (
      <RouteContainer>
        <ErrorHandlingDialog
          isOpen={showErrorDialog}
          onClose={() => toggleErrorDialog(false)}
          onReload={() => window.location.reload()}
        />
      </RouteContainer>
    );
  }
  return (
    <RouteContainer>
      {apiStatus === 'loading' ? (
        <>
          <BypassableRoute path="/" render={() => <CardSkeleton />} />
        </>
      ) : (
        <>
          <BypassableRoute
            path="/"
            render={() => <Redirect to={defaultRoute} />}
          />
          <BypassableRoute
            path={URL_ROUTES.applications}
            render={() => (
              <ApplicationsTab
                key="application-tab"
                type={Array}
                apps={applications}
              />
            )}
          />
          <BypassableRoute
            path={URL_ROUTES.workflows}
            render={() => (
              <WorkflowsTab
                key="workflow-tab"
                type={undefined}
                appList={allApplications[0]}
                appStatus={apiStatus}
                workflowList={workflows}
                workflowStatus={status}
              />
            )}
          />
          <BypassableRoute
            path={URL_ROUTES.aiva}
            render={() => (
              <AivaTab />
            )}
          />
          <BypassableRoute
            path={URL_ROUTES.myunify}
            render={() => (
              <MyUnifyTab
                key="my-unify-tab"
                type={undefined}
                appList={applications}
                appStatus={apiStatus}
                workflowList={myUnifyWorkflows}
                workflowApps={allApplications[0]}
                workflowStatus={status}
              />
            )}
          />
          <BypassableRoute
            path={URL_ROUTES.unifyAdmin}
            render={() => <UnifyMainContainer />}
          />
          <BypassableRoute
            path={UNIFY_ADMIN_ROUTERS.applicationAdmin}
            render={() => <UnifyAdminContainer />}
          />
          <BypassableRoute
            path={UNIFY_ADMIN_ROUTERS.campaignAdmin}
            render={() => <CMSAdminContainer />}
          />
        <BypassableRoute
            path={UNIFY_ADMIN_ROUTERS.productOutageAdmin}
            render={() => <OutageAdminContainer />}
          />
        { uniPulseFlag && adminRole && 
            <BypassableRoute
                path={URL_ROUTES.pulse}
                render={() => <UniPulseMain />}
            />
        }
        { adminRole && 
            <BypassableRoute
                path={"/dealer-admin"}
                render={() => <DealerAdminContainer />}
            />
        }
            <BypassableRoute
              path={URL_ROUTES.globalSearch}
              render={() => <GlobalSearchResult/>}
            />
            <BypassableRoute
              path={URL_ROUTES.globalSearchHistory}
              render={() => <GlobalSearchHistory/>}
            />
          <Route
            exact
            path="/login/callback"
            component={BypassableLoginCallback}
          />
          <Route
            exact
            path="/logout/callback"
            component={LogoutCallback}
          />
        </>
      )}
    </RouteContainer>
  );
}